<template>
    <slot
        name="trigger"
        :open="open"
    />
    <BaseDialog
        v-if="isOpen"
        :is-open="isOpen"
        :size="Size.Small"
        data-spec-class="dialog--confirm"
        @close="close"
    >
        <BaseDialogTitle data-spec-class="dialog--confirm__title">
            <template v-if="titleText">
                {{ t(titleText) }}
            </template>
            <template v-else>
                {{ t('dialogs.confirmation.title') }}
            </template>
        </BaseDialogTitle>

        <BaseDialogBody>
            <!-- eslint-disable vue/no-v-html -->
            <p
                v-if="messageHtml"
                class="text-sm text-tertiary"
                v-html="messageHtml"
            ></p>

            <p
                v-else
                class="text-sm text-tertiary"
            >
                <template v-if="messageText">
                    {{ t(messageText) }}
                </template>
                <!-- eslint-enable vue/no-v-html -->
                <template v-else>
                    {{ t('dialogs.confirmation.message') }}
                </template>
            </p>
        </BaseDialogBody>

        <template #actions>
            <BaseButton
                :is-loading="isLoading"
                :is-disabled="isLoading"
                data-spec-class="dialog--confirm__confirm"
                class="mb-2 w-full sm:mb-0 sm:ml-1 sm:w-fit"
                :theme="theme"
                @click.once="confirm"
            >
                <template v-if="confirmButtonText">
                    {{ t(confirmButtonText) }}
                </template>
                <template v-else>
                    {{ t('actions.confirm') }}
                </template>
            </BaseButton>
            <BaseButton
                :theme="Theme.Secondary"
                data-spec-class="dialog--confirm__cancel"
                class="hidden sm:ml-1 sm:block"
                @click="close"
            >
                {{ t('actions.cancel') }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Size, Theme } from '@/types';

const { t } = useI18n();
interface Props {
    isLoading: boolean;
    theme?: Theme;
    titleText?: string;
    messageText?: string;
    messageHtml?: string;
    confirmButtonText?: string;
}

withDefaults(defineProps<Props>(), {
    theme: Theme.Primary,
    titleText: undefined,
    messageText: undefined,
    messageHtml: undefined,
    confirmButtonText: undefined,
});

const emits = defineEmits(['confirm']);

const isOpen = ref(false);

const open = () => {
    isOpen.value = true;
};

const close = () => {
    isOpen.value = false;
};

const confirm = async () => {
    try {
        await emits('confirm');
        close();
    } catch {
        /* intentionally empty */
    }
};
</script>
